/**
 * Search, AI and link-preview crawlers, recognised by their User-Agent.
 *
 * Why the client needs this: Googlebot and most other renderers run the page's
 * JS with `navigator.language = en-US`. A client that follows the browser
 * language therefore re-renders prawmi.pl in English for the crawler, and the
 * page gets indexed in English (prawmi-9o7bk). Crawlers get the domain default
 * language instead, the same rule the server already applies to the H1
 * (`app/(public)/utils/seoLanguage.ts`).
 *
 * The reference list is the backend's beacon filter,
 * `backend/src/mnb_activity/crawler_filter.py` (`_BOT_UA_TOKENS`); a test keeps
 * this list a superset of it. Deliberately left out: HeadlessChrome and the
 * performance tools (Lighthouse, PageSpeed, GTmetrix). They render pages for a
 * person looking at the result, not for an index, so they keep the visitor
 * behaviour.
 *
 * Distinctive tokens only, matched as case-insensitive substrings. Never a
 * bare "bot": Android device names such as "Cubot" would match it.
 */
export const CRAWLER_UA_TOKENS: readonly string[] = [
  // Google
  'googlebot',
  'google-inspectiontool',
  'google-extended',
  'googleother',
  'adsbot-google',
  'mediapartners-google',
  'storebot-google',
  'apis-google',
  // Microsoft
  'bingbot',
  'bingpreview',
  'microsoftpreview',
  'adidxbot',
  'msnbot',
  // Other search engines
  'duckduckbot',
  'duckassistbot',
  'yandexbot',
  'yandexmobilebot',
  'yandexrenderresourcesbot',
  'baiduspider',
  'applebot',
  'seznambot',
  'petalbot',
  'slurp',
  // AI assistants and model crawlers
  'gptbot',
  'oai-searchbot',
  'chatgpt-user',
  'perplexitybot',
  'perplexity-user',
  'claudebot',
  'claude-web',
  'claude-user',
  'claude-searchbot',
  'anthropic-ai',
  'ccbot',
  'bytespider',
  'amazonbot',
  'meta-externalagent',
  'youbot',
  'ai2bot',
  'cohere-ai',
  // Link previews
  'facebookexternalhit',
  'facebookbot',
  'linkedinbot',
  'twitterbot',
  'slackbot',
  'discordbot',
  'telegrambot',
  // SEO tools
  'semrushbot',
  'ahrefsbot',
  'mj12bot',
  'dotbot',
  'dataforseobot',
  'barkrowler',
  // Generic, as in the backend list
  'crawler',
  'spider',
];

/**
 * True when the user agent belongs to a known crawler or renderer. Without an
 * argument it reads `navigator.userAgent`; with no navigator (server) it is
 * false.
 */
export const isCrawlerUserAgent = (userAgent?: string | null): boolean => {
  const value =
    userAgent ?? (typeof navigator === 'undefined' ? null : navigator.userAgent);
  if (!value) return false;
  const lowered = value.toLowerCase();
  return CRAWLER_UA_TOKENS.some((token) => lowered.includes(token));
};
